import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { FaCloud, FaLayerGroup, FaMapMarkerAlt, FaUserCircle } from 'react-icons/fa';
import ResourceSummary from '../graph/ResourceSummary';
import ResourceDistribution from '../graph/ResourceDistribution';
import CloudMapTrends from '../graph/CloudMapTrends';
import ResourceByRegion from '../graph/ResourceByRegion';
import ResourceInsights from '../graph/ResourceInsights';

const OrganizationResourceView = ({ resources, summary, trendsData, cloudAccounts, onAccountSelect, onBack, title }) => {
  const accountStats = useMemo(() => {
    const stats = {};
    resources.forEach(resource => {
      const accountId = resource.metadata?.accountId;
      if (accountId) {
        if (!stats[accountId]) {
          stats[accountId] = { count: 0, types: new Set(), regions: new Set() };
        }
        stats[accountId].count++;
        stats[accountId].types.add(resource.type);
        stats[accountId].regions.add(resource.region);
      }
    });
    return stats;
  }, [resources]);

  const uniqueRegions = useMemo(() => {
    return [...new Set(resources.map(r => r.region).filter(Boolean))];
  }, [resources]);

  const uniqueAccounts = useMemo(() => {
    return [...new Set(resources.map(r => r.metadata?.accountId).filter(Boolean))];
  }, [resources]);

  const getAccountAlias = (accountId) => {
    const account = cloudAccounts.find(a => a.awsAccountId === accountId);
    return account?.alias || account?.description || accountId;
  };

  return (
    <div className="container">
      {onBack && (
        <button onClick={onBack} className="btn btn-link mb-3 p-0" style={{ color: '#6D5BFF', textDecoration: 'none' }}>
          ← Back to All Organizations
        </button>
      )}
      {title && <h4 className="mb-4" style={{ fontWeight: '600', color: '#1e293b' }}>{title}</h4>}
      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)',
            color: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(109, 91, 255, 0.2)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                <FaCloud />
              </div>
              <h3 className="mb-0">{summary?.totalResources || 0}</h3>
              <small style={{ opacity: 0.9 }}>Total Resources</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                <FaLayerGroup />
              </div>
              <h3 className="mb-0">{summary?.resourceTypes?.length || 0}</h3>
              <small style={{ opacity: 0.9 }}>Resource Types</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                <FaMapMarkerAlt />
              </div>
              <h3 className="mb-0">{uniqueRegions.length}</h3>
              <small style={{ opacity: 0.9 }}>Regions</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ 
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                <FaUserCircle />
              </div>
              <h3 className="mb-0">{uniqueAccounts.length}</h3>
              <small style={{ opacity: 0.9 }}>Accounts</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="mb-4">
        <Col md={6}>
          <CloudMapTrends trendsData={trendsData} />
        </Col>
        <Col md={6}>
          <ResourceByRegion resources={resources} />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <ResourceDistribution summary={summary} />
        </Col>
        <Col md={6}>
          <ResourceInsights />
        </Col>
      </Row>

      {/* Account Cards */}
      <div className="mb-3">
        <h5 style={{ color: '#1e293b', fontWeight: '600' }}>AWS Accounts</h5>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
          Click on an account to view detailed resources
        </p>
      </div>

      <Row>
        {uniqueAccounts.map((accountId, idx) => {
          const stats = accountStats[accountId] || {};
          return (
            <Col md={4} key={idx} className="mb-3">
              <Card
                onClick={() => onAccountSelect(accountId)}
                style={{
                  background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(109, 91, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(109, 91, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(109, 91, 255, 0.2)';
                }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <div style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '50%', 
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      marginRight: '12px'
                    }}>
                      <FaUserCircle />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-0" style={{ 
                        fontSize: '0.875rem', 
                        opacity: 0.9,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {getAccountAlias(accountId)}
                      </h6>
                      <small style={{ opacity: 0.8, fontSize: '0.75rem' }}>{accountId}</small>
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.15)', 
                    borderRadius: '8px', 
                    padding: '12px',
                    marginBottom: '8px'
                  }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Resources</span>
                      <strong style={{ fontSize: '1.25rem' }}>{stats.count || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Types</span>
                      <strong>{stats.types?.size || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Regions</span>
                      <strong>{stats.regions?.size || 0}</strong>
                    </div>
                  </div>
                  
                  <div className="text-center" style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                    Click to view details →
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default OrganizationResourceView;
